import { apiPost } from '../shared/utils/apiClient'
import { API_CONFIG } from './config/api'

export interface UploadImageResponse {
  success: boolean
  url: string
  filename: string
  size: number
  mimetype: string
}

export interface UploadImageOptions {
  folder?: string
  onProgress?: (progress: number) => void
}

/**
 * Upload image to DigitalOcean Spaces via backend API
 * @param file - Image file to upload
 * @param options - Upload options (folder, progress callback)
 * @returns Promise with upload response containing image URL
 */
export async function uploadImageApi(
  file: File,
  options?: UploadImageOptions,
): Promise<UploadImageResponse> {
  const formData = new FormData()
  formData.append('file', file)

  let endpoint = API_CONFIG.endpoints.storage.upload
  if (options?.folder) {
    endpoint += `?folder=${encodeURIComponent(options.folder)}`
  }

  let response: Response
  try {
    response = await apiPost(endpoint, formData)
  } catch {
    throw 'UPLOAD_NETWORK_ERROR' as const
  }

  if (!response.ok) {
    throw 'UPLOAD_HTTP_ERROR' as const
  }

  return await response.json()
}

export type ImageValidationError = 'INVALID_FILE_TYPE' | 'FILE_TOO_LARGE'
export type UploadApiError = 'UPLOAD_NETWORK_ERROR' | 'UPLOAD_HTTP_ERROR'

/**
 * Validate image file before upload
 * @param file - File to validate
 * @param maxSizeMB - Maximum file size in MB (default: 5MB)
 * @returns Error code or null if valid
 */
export function validateImageFile(
  file: File,
  maxSizeMB = 5,
): ImageValidationError | null {
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

  if (!allowedTypes.includes(file.type)) {
    return 'INVALID_FILE_TYPE'
  }

  const maxSizeBytes = maxSizeMB * 1024 * 1024
  if (file.size > maxSizeBytes) {
    return 'FILE_TOO_LARGE'
  }

  return null
}
